import React, { useLayoutEffect } from 'react';
import { IngestWithContext } from '@wealthlane/chatbot';
import {
  axiosInstance,
  registerAuthenticatedHomeTenantId,
  resolveRequestTenantId,
  unregisterAuthenticatedHomeTenantId,
} from '../../services/apiInterceptor';
import { toast } from 'react-toastify';
import ToastComponent from '../toast';
import { useNavigate } from 'react-router-dom';
import useInstance from '../../hooks/custom-hooks/useInstance';
import { getTenantId } from '@apps/customer/utils/tenantHelpers';
import { configState, devLogger } from '../chatbot/config';
import useWealthlaneCustomerApi from '../../hooks/useWealthlaneCustomerApi';
import useGetCurrentTenantInfoEnabled from '../../hooks/get/useGetCurrentTenantInfoEnabled';
import { useSelector } from 'react-redux';
import { selectToken } from '../../pages/auth/authSlice';

function IngestComponent() {
  const navigate = useNavigate();
  const { getCurrentCid } = useInstance();
  const cid = getCurrentCid();
  const token = useSelector(selectToken);
  const isAuth = Boolean(token);
  const { handleRequest: handleCustomerRequest } = useWealthlaneCustomerApi();
  const { data: currentTenantInfo } = useGetCurrentTenantInfoEnabled(
    handleCustomerRequest,
    isAuth
  );

  useLayoutEffect(() => {
    registerAuthenticatedHomeTenantId('ingest-page', currentTenantInfo?.id);
    return () => unregisterAuthenticatedHomeTenantId('ingest-page');
  }, [currentTenantInfo?.id]);

  const resolveTenantId = (): string =>
    getTenantId() || currentTenantInfo?.id || resolveRequestTenantId();
  const tenantId = resolveTenantId();

  const restUrl = (
    process.env.REACT_APP_INGESTION_ENDPOINT ||
    process.env.REACT_APP_CHATBOT_REST_URL ||
    ''
  ).replace(/\/$/, '');

  if (!isAuth || !tenantId) {
    return null;
  }

  return (
    <IngestWithContext
      axiosInstance={axiosInstance}
      config={configState}
      navigate={navigate}
      goBackAccessor={() => navigate(-1)}
      companyId={cid?.toString?.() ?? ''}
      tenantIdAccessor={resolveTenantId}
      logger={devLogger}
      alertFunctionSuccess={(message: string) => {
        toast.success(<ToastComponent label='Success' message={message} />);
      }}
      alertFunctionError={(message: string) => {
        toast.error(<ToastComponent label='Error' message={message} />);
      }}
      metadataDocumentLink={`${restUrl}/ingest/document/metadata/list?status=uploaded`}
      createDocumentLink={`${restUrl}/generate-presigned-url`}
      uploadCompleteLink={`${restUrl}/ingest/upload/complete`}
      previewDocumentLink={`${restUrl}/get-preview-presigned-url`}
      saveDocumentLink={`${restUrl}/ingest/publish`}
      documentListLink={`${restUrl}/ingest/document/list`}
      setAsActiveDocumentLink={`${restUrl}/ingest/document/version/rollback`}
      authoringScopeLink={`${restUrl}/ingest/authoring-scope`}
      submitReviewLink={`${restUrl}/ingest/document/submit-review`}
      reviewDocumentLink={`${restUrl}/ingest/document/review`}
      publishApprovedLink={`${restUrl}/ingest/document/publish-approved`}
      knowledgeWorksheetTemplateLink={`${restUrl}/ingest/knowledge-worksheet/template`}
      knowledgeWorksheetPreviewLink={`${restUrl}/ingest/knowledge-worksheet/preview`}
      knowledgeWorksheetCommitLink={`${restUrl}/ingest/knowledge-worksheet/commit`}
      knowledgeArticleDetailLink={`${restUrl}/ingest/knowledge-article`}
      knowledgeArticleReviseLink={`${restUrl}/ingest/knowledge-article/revise`}
      documentBulkActionLink={`${restUrl}/ingest/document/bulk-action`}
      defaultAudienceScope='admin'
      transcribeAudioLink={process.env.REACT_APP_CHATBOT_AUDIO_TRANSCRIBE_URL || ''}
      chunkPreviewLink={`${restUrl}/ingest/document/chunks`}
      sourceDetailLink={`${restUrl}/ingest/sources`}
      chunkValidateLink={`${restUrl}/ingest/chunk/validate`}
      chunkEditLink={`${restUrl}/ingest/chunk/edit`}
      chunkPromoteLink={`${restUrl}/ingest/chunk/promote`}
      chunkRevertLink={`${restUrl}/ingest/chunk/revert`}
      chunkPendingLink={`${restUrl}/ingest/chunk/pending`}
      chunkDiscardDraftsLink={`${restUrl}/ingest/chunks/discard-drafts`}
      chunkArchiveLink={`${restUrl}/ingest/chunks/archive`}
      chunkTestQueryLink={`${restUrl}/ingest/chunk/test-query`}
      suggestLink={`${restUrl}/ingest/suggest`}
      publishLink={`${restUrl}/ingest/publish`}
      taxonomyFacetsLink={`${restUrl}/taxonomy/facets`}
      taxonomyOptionsLink={`${restUrl}/taxonomy/options`}
      versionInfoLink={`${restUrl}/ingest/document/version/list?logical_id=`}
      promoteDocumentLink={`${restUrl}/ingest/document/update-status`}
      ingestJobsLink={`${restUrl}/ingest/jobs`}
      allowPromote={true}
      allowTest={true}
      showAdvancedKnowledgeTools={false}
      cid={cid?.toString?.() ?? ''}
    />
  );
}

export const Ingest = IngestComponent;
